import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import networkHandler from "../../../network/networkHandler";
import { EApiMiddlewareMethods } from "../../../network/networkHandler.types";
import { HubWiseState } from "./hubWiseSlice.types";
import AppConstants from "../../../constants";
import { transformSelectValues } from "../../../utils/helpers.utils";
import customNetworHandler from "network/customNetworHandler";
import { cWingsServiceBaseURL } from "config/Api.config";
import networkHandlerReporting from "network/networkHandlerReporting";

export const fetchFiltersHubWise = createAsyncThunk(
    "HubWiseDashboard/FetchFiltersHubWise",
    async (params, { rejectWithValue }) => {
        try {
            const request = {
                url: `fulfilment/filter/get/all/${sessionStorage.getItem(AppConstants.COUNTRY_CODE)}/ORDER`,
                method: EApiMiddlewareMethods.GET,
              } as any;
              request.headers = { countryISOCode: sessionStorage.getItem(AppConstants.COUNTRY_CODE) };
              const { data } = await networkHandlerReporting(request);
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchConsignmentsSlotsHubWise = createAsyncThunk(
    "HubWiseDashboard/FetchConsignmentsSlotsHubWise",
    async (
        {
            params,
        }: {
            params: any;
        },
        { rejectWithValue }
    ) => {
        try {
            const request = {
                url: "/fulfilment/operations/dashboard/consignments/slots",
                method: EApiMiddlewareMethods.POST,
                data: params,
            } as any;
            const { data } = await customNetworHandler(request, cWingsServiceBaseURL);
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const fetchHubWiseData = createAsyncThunk(
    "HubWiseDashboard/FetchHubWiseData",
    async (
        {
            params,
        }: {
            params: any;
        },
        { rejectWithValue }
    ) => {
        try {
            const request = {
                url: "/fulfilment/operations/dashboard/consignments/hubWise",
                method: EApiMiddlewareMethods.POST,
                data: params,
            } as any;
            const { data } = await customNetworHandler(request, cWingsServiceBaseURL);
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

const initialState: HubWiseState = {
    loading: false,
    hubWiseDataLoading: false,
    filtersDropdowns: {
        countriesList: [],
        hubCodeList: [],
        hubNameList: [],
        deliveryAreaList: [],
        deliverySlotList: [],
        orderTypeList: []
    },
    allSlots: [],
    hubWiseDataList: [],
};

const hubWiseSlice = createSlice({
    name: "HubWiseDashboard",
    initialState,
    reducers: {},
    extraReducers: (builders) => {
        builders
            .addCase(fetchFiltersHubWise.pending, (state) => {
                state.loading = true;
                state.errorCode = "";
                state.error = "";
            })
            .addCase(fetchFiltersHubWise.fulfilled, (state, action) => {
                const {
                    payload
                } = action;
                let hubCodeList: any = {};
                let hubNameList: any = {};
                let deliveryAreaList: any = {};
                let deliverySlotList: any = {};
                let orderTypeList: any = {};
                if (payload.hubMap) {
                    Object.keys(payload.hubMap).forEach((country: any) => {
                        hubCodeList[country] = transformSelectValues(payload.hubMap[country], 'code');
                        hubNameList[country] = transformSelectValues(payload.hubMap[country], 'value');
                    });
                }
                if (payload.deliveryArea) {
                    Object.keys(payload.deliveryArea).forEach((country: any) => {
                        deliveryAreaList[country] = transformSelectValues(payload.deliveryArea[country]);
                    });
                }
                if (payload.deliverySlot) {
                    Object.keys(payload.deliverySlot).forEach((country: any) => {
                        deliverySlotList[country] = transformSelectValues(payload.deliverySlot[country]);
                    });
                }
                if (payload.orderType) {
                    Object.keys(payload.orderType).forEach((country: any) => {
                        orderTypeList[country] = transformSelectValues(payload.orderType[country]);
                    });
                }
                state.loading = false;
                state.errorCode = "";
                state.error = "";
                state.filtersDropdowns = {
                    countriesList: transformSelectValues(payload.countries),
                    hubCodeList: hubCodeList,
                    hubNameList: hubNameList,
                    deliveryAreaList: deliveryAreaList,
                    deliverySlotList: deliverySlotList,
                    orderTypeList: orderTypeList
                };
            })
            .addCase(fetchFiltersHubWise.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(fetchConsignmentsSlotsHubWise.pending, (state) => {
                state.errorCode = "";
                state.error = "";
                state.loading = true;
            })
            .addCase(fetchConsignmentsSlotsHubWise.fulfilled, (state, action) => {
                const { payload } = action;
                state.errorCode = "";
                state.error = "";
                state.loading = false;
                if (payload.allSlots.length) {
                    state.allSlots = transformSelectValues(payload.allSlots);
                }
            })
            .addCase(fetchConsignmentsSlotsHubWise.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.loading = false;
                state.errorCode = errorPayload
                    ? errorPayload.error
                    : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
            .addCase(fetchHubWiseData.pending, (state) => {
                state.errorCode = "";
                state.error = "";
                state.hubWiseDataLoading = true;
            })
            .addCase(fetchHubWiseData.fulfilled, (state, action) => {
                const { payload } = action;
                state.errorCode = "";
                state.error = "";
                state.hubWiseDataLoading = false;
                state.hubWiseDataList = payload.hubWise;
                if (payload?.allSlots?.length) {
                    state.allSlots = transformSelectValues(payload.allSlots);
                }
            })
            .addCase(fetchHubWiseData.rejected, (state, action) => {
                const errorPayload: any = action.payload;
                state.hubWiseDataLoading = false;
                state.errorCode = errorPayload
                    ? errorPayload.error
                    : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
                state.error = errorPayload
                    ? errorPayload.message || errorPayload.status
                    : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
            })
    },
});

export default hubWiseSlice.reducer;
